import React from 'react';
import { Avatar, Badge, Box, Button, Grid, Typography, IconButton } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { formatDuration, intervalToDuration } from 'date-fns';
import { Link, useRouteMatch, useParams } from 'react-router-dom';
import { UserContext } from 'contexts/user/user-context';
import EditButton from 'components/edit-button/edit-button';
import IconSection from 'components/icon-section';
import Icon from 'components/icon';
import DropMenu from 'components/drop-menu';
import ProfileEdit from '../edit';
import getProfileEditRoutes from '../edit/profile-edit-routes';


const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    padding: theme.spacing(5),
    [theme.breakpoints.down('md')] : {
      padding: theme.spacing(4),
    }
  },
  avatarContainer: {
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    '& $avatar': {
      fontSize: theme.typography.sizes[1]
    },
    [theme.breakpoints.down('md')] : {
      flexDirection: 'row',
      // alignItems: 'flex-start',
      textAlign: 'left',
      paddingTop: theme.spacing(2),
    }
  },
  avatar: {
    lineHeight: '1rem',
    width: theme.spacing(16),
    height: theme.spacing(16),
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    [theme.breakpoints.down('md')] : {
      width: theme.spacing(14),
      height: theme.spacing(14),
      marginBottom: 0,
    },
    [theme.breakpoints.down('sm')] : {
      width: theme.spacing(12),
      height: theme.spacing(12),
    },
    '& img': {
      opacity: 1,
      transition: `200ms ${theme.transitions.easing.easeOut}`,
      borderRadius: '50%', // hack for iOS which shows square avatar on tap
    }
  },
  avatarText: {
    alignItems: 'center',
    [theme.breakpoints.down('md')] : {
      marginLeft: theme.spacing(3),
      alignItems: 'flex-start',
    }
  },
  avatarBadge: {
    '&:hover img': {
      opacity: 0.5,
    },
    '&:hover button': {
      opacity: 1
    },
    textDecoration: 'none',
  },
  name: {
    lineHeight: '1.2',
    marginBottom: 4,
  },
  editPhotoButton: {
    padding: theme.spacing(0.5),
    minWidth: 'auto',
    borderRadius: '50%',
    opacity: 0,
  },
  editMenu: {
    position: 'absolute',
    top: 0,
    right: 0,
    padding: theme.spacing(4),
    [theme.breakpoints.down('sm')] : {
      padding: theme.spacing(1),
    },
  },
  docsButton: {
    position: 'relative',
    bottom: '4px',
    right: '8px',
    textTransform: 'none',
    fontWeight: 'normal',
    textAlign: 'left',
  },
}));


export default function UserView(props) {
  const { user: profileUser, setUser: setProfileUser } = props;
  const classes = useStyles();
  const { user } = React.useContext(UserContext);
  const { url } = useRouteMatch();  
  const { userId } = useParams();
  const isTrainerViewingTrainee = user.role.isTrainer && userId > 0 && user.id !== profileUser.id;
  const profileEditRoutes = getProfileEditRoutes(profileUser, user);


  const getPathForKey = (key) => {
    return profileEditRoutes.find(r => r.key === key)?.path;
  }


  const avatarBase = profileUser && (
    <Avatar
      className={classes.avatar}
      alt="Department Logo"
      src={profileUser?.departmentLogoUrl}
      children={'Add Department Badge/Logo'}
    />
  );


  const avatar = !avatarBase 
    ? ''
    : isTrainerViewingTrainee
      ? avatarBase
      : <Badge
          component={Link}
          to={`${url}/edit${getPathForKey('photo')}`}
          overlap="circular"
          className={classes.avatarBadge}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
          badgeContent={
            <Button
              variant="contained"
              size="small"
              className={classes.editPhotoButton}
            >
              <Icon type='edit' />
            </Button>
          }
        >
          {avatarBase}
        </Badge>;
        
  const documentCount = profileUser?.documents.reduce((sum, curr) => sum + curr.files.length, 0);
  const membershipDuration = (() => {
    const start = new Date(profileUser?.signupDate || null);
    const end = new Date();
    const format = ['years', 'months'];
    return formatDuration(intervalToDuration({ start, end }), { format, delimiter: ', ' }) || 'first month';
  })();


  return <>
    {!isTrainerViewingTrainee &&
    <div className={classes.editMenu}>
      <EditButton
        minBreakpoint="xs"
        iconOnly
        to={`${url}/edit${getPathForKey('details')}`}
      />
      <DropMenu
        aria-label="user edit options"
        label={<IconButton size="large"><Icon type='menu' /></IconButton>}
        items={profileEditRoutes.map(r => ({
          key: r.key,
          name: r.label,
          icon: r.iconName,
          component: Link,
          to: `${url}/edit${r.path}`
        }))}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      />
    </div>}
    <Grid container spacing={4} direction="row">

      {/* AVATAR */}
      <Grid item xs={12} md={4}>
        <Box display="flex" className={classes.avatarContainer}>
          {avatar}
          <Box display="flex" flexDirection="column" className={classes.avatarText}>
            <Typography variant="h6" className={classes.name}><b>{profileUser?.name}</b></Typography>
            <Typography variant="body1">{profileUser?.departmentName}</Typography>
          </Box>
        </Box>          
      </Grid>

      <Grid item xs={12} md={8}>

        {/* PROFILE DETAILS */}
        <IconSection iconName="profile">
          <Box py={0.25} data-testid="profile-details">
            <div>{profileUser?.name}</div>
            <div>{profileUser?.email}</div>
            <div>{profileUser?.phone}</div>
          </Box>
        </IconSection>

        {/* DEPARTMENT DETAILS */}
        <IconSection iconName="department">
          <Box py={0.25} data-testid="profile-department">
            <div>{profileUser?.departmentName}</div>
            <div>{profileUser?.badge ? `Badge ${profileUser.badge}` : ''}</div>
            <div>{profileUser?.dutyAssignment}</div>
          </Box>
        </IconSection>

        {/* SUMMARY */}
        <IconSection iconName="summary">
          <Box py={0.25}>
            <div>PACKTRACK Member: {membershipDuration}</div>
            <div>Roles: {profileUser?.availableRoles?.map(role => role.name).join(', ')}</div>
          </Box>
        </IconSection>

        {/* STATS */}
        <IconSection iconName="stats">
          <Box py={0.25}>
            <div>{profileUser?.trainingHours} Training Hours</div>
            <div>{profileUser?.deploymentCount} Deployments</div>
          </Box>
        </IconSection>

        {/* DOCUMENTS */}
        {(!isTrainerViewingTrainee && profileUser?.documents) &&
        <IconSection iconName="user-guide">
          <Box py={0.25}>
            <Button 
              component={Link}
              to={`${url}/edit${getPathForKey('documents')}`}
              className={classes.docsButton}
            >
              {documentCount === 0 
                ? '0 documents'
                : `${documentCount} document${documentCount > 1 ? 's' : ''} in ${profileUser.documents.length} categor${profileUser.documents.length > 1 ? 'ies': 'y'}`
              }
            </Button>
          </Box>
        </IconSection>}
      </Grid>
    </Grid>
  
    {!isTrainerViewingTrainee &&
    <ProfileEdit user={profileUser} setUser={setProfileUser} />}
  </>;
}